import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography, Box, Button, Paper, Accordion, AccordionSummary, AccordionDetails,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  TextField, MenuItem, FormControl, InputLabel, Select,
  Grid, CircularProgress, Stack, useTheme, alpha, Chip, Divider,
  Card, CardContent, Container
} from '@mui/material';
import { Add, Edit, Delete, ExpandMore, Print as PrintIcon, Science as ScienceIcon, Widgets as WidgetsIcon } from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { convertUnits, getUnitType } from '../../../utils/unitConversions';
import { usePermissions } from '../../../utils/permissions';

const IngredientsTab = ({ formulation, rawMaterials, phases, showSnackbar, formulationIngredients: initialIngredients = [], user, onUpdate }) => {
  const theme = useTheme();
  const { canAccessPage, canEditPage, hasPermission, isManagerOrAbove, userRole, hierarchyLevel } = usePermissions();
  const [selectedPhase, setSelectedPhase] = useState(null);
  const [selectedIngredient, setSelectedIngredient] = useState({});
  const [ingredientDialogOpen, setIngredientDialogOpen] = useState(false);
  const [ingredientDialogMode, setIngredientDialogMode] = useState('create');
  const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
  const [selectedPhaseData, setSelectedPhaseData] = useState({ name: '', description: '' });
  const [phaseDialogMode, setPhaseDialogMode] = useState('create');
  const [loading, setLoading] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [deletePhaseId, setDeletePhaseId] = useState(null);
  const [deleteIngredientConfirmOpen, setDeleteIngredientConfirmOpen] = useState(false);
  const [deleteIngredientId, setDeleteIngredientId] = useState(null);
  const [availableIngredients, setAvailableIngredients] = useState([]);
  const [formulationIngredients, setFormulationIngredients] = useState(initialIngredients);
  
  const [ingredientSuppliers, setIngredientSuppliers] = useState([]);
  const [supplierFetchLoading, setSupplierFetchLoading] = useState(false);
  
  const [newIngredientDialogOpen, setNewIngredientDialogOpen] = useState(false);
  const [newIngredientData, setNewIngredientData] = useState({
    name: '',
    inci_name: '',
    cas_number: '',
    description: ''
  });
  
  const [ingredientSelectionDialogOpen, setIngredientSelectionDialogOpen] = useState(false);
  const [ingredientSearchQuery, setIngredientSearchQuery] = useState('');

  const navigate = useNavigate();

  // Helper function to get saved state from localStorage
  const getSavedExpandedState = (phaseId) => {
    if (!formulation?.id) return false;
    try {
      const storageKey = `ingredients_formulation_${formulation.id}_phase_${phaseId}_expanded`;
      const saved = localStorage.getItem(storageKey);
      return saved !== null ? JSON.parse(saved) : false;
    } catch {
      return false;
    }
  };

  // State for tracking expanded accordions - initialize with saved states
  const [expandedPhases, setExpandedPhases] = useState(() => {
    const initialState = {};
    if (formulation?.id && phases.length > 0) {
      phases.forEach(phase => {
        initialState[phase.id] = getSavedExpandedState(phase.id);
      });
    }
    return initialState;
  });

  // Update expanded states when phases change
  useEffect(() => {
    if (formulation?.id && phases.length > 0) {
      setExpandedPhases(prev => {
        const newState = { ...prev };
        phases.forEach(phase => {
          if (!(phase.id in newState)) {
            newState[phase.id] = getSavedExpandedState(phase.id);
          }
        });
        return newState;
      });
    }
  }, [formulation?.id, phases]);

  // Save individual phase state to localStorage whenever it changes
  useEffect(() => {
    if (formulation?.id) {
      Object.entries(expandedPhases).forEach(([phaseId, isExpanded]) => {
        const storageKey = `ingredients_formulation_${formulation.id}_phase_${phaseId}_expanded`;
        localStorage.setItem(storageKey, JSON.stringify(isExpanded));
      });
    }
  }, [expandedPhases, formulation?.id]);

  // Handle accordion expand/collapse
  const handleAccordionChange = (phaseId) => (event, isExpanded) => {
    setExpandedPhases(prev => ({
      ...prev,
      [phaseId]: isExpanded
    }));
  };

  useEffect(() => {
    if (rawMaterials && rawMaterials.length > 0) {
      setAvailableIngredients(rawMaterials);
    } else {
      fetchIngredientsFromDb();
    }
  }, [rawMaterials]);

  useEffect(() => {
    setFormulationIngredients(initialIngredients);
  }, [initialIngredients]);
  
  const handlePrintIngredientList = () => {
    // Remove any existing print frames to prevent leakage
    const existingFrames = document.querySelectorAll('iframe.print-frame');
    existingFrames.forEach(frame => {
      document.body.removeChild(frame);
    });
    
    // Save the original document title
    const originalTitle = document.title;
    
    // Calculate total percentage
    const totalPercentage = formulationIngredients.reduce((sum, item) => sum + parseFloat(item.percentage || 0), 0);
    
    // Group ingredients by phase
    const ingredientsByPhase = {};
    
    // Initialize with empty arrays for all phases
    phases.forEach(phase => {
      ingredientsByPhase[phase.id] = [];
    });
    
    // Add ingredients to their phases
    formulationIngredients.forEach(ingredient => {
      const phaseId = ingredient.formulation_phase_id || 'unassigned';
      if (!ingredientsByPhase[phaseId]) {
        ingredientsByPhase[phaseId] = [];
      }
      ingredientsByPhase[phaseId].push(ingredient);
    });
    
    // Change the document title to remove React App from header
    document.title = "Ingredients List";
    
    // Create the iframe for clean printing
    const printFrame = document.createElement('iframe');
    printFrame.style.position = 'absolute';
    printFrame.style.top = '-9999px';
    printFrame.style.left = '-9999px';
    printFrame.style.width = '0';
    printFrame.style.height = '0';
    printFrame.style.border = '0';
    printFrame.className = 'print-frame';
    document.body.appendChild(printFrame);
    
    // Prepare the content and styles
    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Ingredients List</title>
          <style>
            /* Reset all margins and padding */
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            
            body {
              font-family: Arial, sans-serif;
              font-size: 10pt;
              line-height: 1.2;
              color: #000;
              background: #fff;
              width: 100%;
              padding: 5mm;
            }
            
            /* Page setup */
            @page {
              size: auto;
              margin: 5mm;
            }
            
            /* Confidential marker */
            .confidential-marker {
              position: absolute;
              top: 2mm;
              left: 5mm;
              font-size: 14pt;
              font-weight: bold;
              color: #cc0000;
              border: 1px solid #cc0000;
              padding: 2px 6px;
              transform: rotate(-5deg);
              opacity: 0.9;
              letter-spacing: 1px;
            }
            
            .header {
              text-align: center;
              border-bottom: 1px solid #000;
              padding-bottom: 5px;
              margin-bottom: 10px;
              margin-top: 10mm; /* Space for confidential marker */
            }
            
            .header h1 {
              font-size: 14pt;
              font-weight: bold;
              margin-bottom: 5px;
            }
            
            .meta {
              display: flex;
              flex-wrap: wrap;
              justify-content: space-between;
              font-size: 9pt;
            }
            
            .meta-item {
              width: 48%;
              margin-bottom: 3px;
            }
            
            .info-box {
              border: 1px solid #ccc;
              padding: 5px;
              margin: 5px 0;
              background-color: #f9f9f9;
              font-size: 9pt;
            }
            
            .info-item {
              margin: 2px 0;
            }
            
            .phase {
              margin: 8px 0;
            }
            
            .phase-header {
              font-weight: bold;
              background-color: #eee;
              padding: 4px;
              border: 1px solid #ddd;
              font-size: 10pt;
            }
            
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 8pt;
              margin: 3px 0 8px 0;
            }
            
            th, td {
              border: 1px solid #ddd;
              padding: 3px;
              text-align: left;
            }
            
            th {
              background-color: #f0f0f0;
              font-weight: bold;
            }
            
            .percentage {
              text-align: right;
            }
            
            .total-row {
              font-weight: bold;
              border-top: 1px solid #000;
            }
            
            .footer {
              font-size: 7pt;
              color: #666;
              text-align: center;
              margin-top: 10px;
              border-top: 1px solid #ccc;
              padding-top: 3px;
            }
            
            .compact-cell {
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              max-width: 150px;
            }
          </style>
        </head>
        <body>
          <div class="confidential-marker">CONFIDENTIAL</div>
          
          <div class="header">
            <h1>Ingredients List</h1>
            <div class="meta">
              <div class="meta-item"><strong>Formulation:</strong> ${formulation?.name || 'Untitled'}</div>
              <div class="meta-item"><strong>Code:</strong> ${formulation?.code || 'N/A'}</div>
              <div class="meta-item"><strong>Version:</strong> ${formulation?.version || '1.0'}</div>
              <div class="meta-item"><strong>Date:</strong> ${new Date().toLocaleDateString()}</div>
              <div class="meta-item"><strong>Batch Size:</strong> ${formulation?.target_batch_size || '100'} ${formulation?.unit_of_measure || 'g'}</div>
            </div>
          </div>
          
          <div class="info-box">
            <div class="info-item"><strong>Total Percentage:</strong> ${totalPercentage.toFixed(2)}%</div>
            <div class="info-item"><strong>Number of Phases:</strong> ${phases.length}</div>
            <div class="info-item"><strong>Number of Ingredients:</strong> ${formulationIngredients.length}</div>
          </div>
          
          ${phases.map(phase => `
            <div class="phase">
              <div class="phase-header">
                ${phase.name}
                ${phase.description ? `<div style="font-size:8pt;font-weight:normal;">${phase.description}</div>` : ''}
              </div>
              
              <table>
                <thead>
                  <tr>
                    <th style="width:5%;">#</th>
                    <th style="width:25%;">Ingredient</th>
                    <th style="width:15%;">Supplier</th>
                    <th style="width:15%;">Description</th>
                    <th style="width:10%;">CAS Number</th>
                    <th style="width:8%;">%</th>
                    <th style="width:22%;">Function</th>
                  </tr>
                </thead>
                <tbody>
                  ${ingredientsByPhase[phase.id]
                    .sort((a, b) => (a.sequence || 999) - (b.sequence || 999))
                    .map((item, index) => {
                      const ingredientData = availableIngredients.find(ing => ing.id === item.ingredient_id) || {};
                      // Find supplier information using the same logic as table display
                      let supplierInfo = '—';
                      
                      // Try to extract supplier from notes first
                      let selectedSupplier = null;
                      try {
                        if (item.notes) {
                          const notesData = JSON.parse(item.notes);
                          if (notesData.selected_supplier) {
                            // Find the selected supplier in our suppliers list
                            selectedSupplier = ingredientSuppliers.find(s => s.id === notesData.selected_supplier);
                          }
                        }
                      } catch (e) {
                        // Notes is not JSON, ignore
                      }
                      
                      // Show selected supplier if found
                      if (selectedSupplier && selectedSupplier.suppliers) {
                        supplierInfo = selectedSupplier.suppliers.name;
                      } else {
                        // Fall back to the preferred supplier for this material
                        const materialSuppliers = ingredientSuppliers.filter(s => s.material_id === item.ingredient_id);
                        if (materialSuppliers.length > 0) {
                          const preferredSupplier = materialSuppliers.find(s => s.is_preferred) || materialSuppliers[0];
                          if (preferredSupplier && preferredSupplier.suppliers) {
                            supplierInfo = preferredSupplier.suppliers.name;
                          }
                        }
                      }
                      return `
                        <tr>
                          <td>${index + 1}</td>
                          <td class="compact-cell">${ingredientData.name || 'Unknown'}</td>
                          <td class="compact-cell">${supplierInfo}</td>
                          <td class="compact-cell">${ingredientData.description || '-'}</td>
                          <td>${ingredientData.cas_number || '-'}</td>
                          <td class="percentage">${parseFloat(item.percentage || 0).toFixed(2)}%</td>
                          <td class="compact-cell">${item.function || '-'}</td>
                        </tr>
                      `;
                    }).join('')}
                </tbody>
              </table>
            </div>
          `).join('')}
          
          <div class="phase">
            <div class="phase-header">
              Total Usage per Ingredient (across all phases)
            </div>
            
            <table>
              <thead>
                <tr>
                  <th style="width:5%;">#</th>
                  <th style="width:25%;">Ingredient</th>
                  <th style="width:15%;">Supplier</th>
                  <th style="width:15%;">Description</th>
                  <th style="width:10%;">CAS Number</th>
                  <th style="width:8%;">Total %</th>
                  <th style="width:12%;">Total Amount</th>
                  <th style="width:10%;">Phases</th>
                </tr>
              </thead>
              <tbody>
                ${(() => {
                  // Aggregate ingredients by ingredient_id for print
                  const aggregatedIngredients = {};
                  
                  formulationIngredients.forEach(ingredient => {
                    const key = ingredient.ingredient_id;
                    if (!aggregatedIngredients[key]) {
                      const ingredientData = availableIngredients.find(ing => ing.id === ingredient.ingredient_id) || {};
                      aggregatedIngredients[key] = {
                        ingredient_id: ingredient.ingredient_id,
                        ingredient_name: ingredientData.name || 'Unknown Ingredient',
                        description: ingredientData.description || '—',
                        cas_number: ingredientData.cas_number || '—',
                        total_percentage: 0,
                        phases_used: [],
                        supplier_info: '—'
                      };
                    }
                    
                    aggregatedIngredients[key].total_percentage += parseFloat(ingredient.percentage || 0);
                    
                    // Add phase name to the list
                    const phase = phases.find(p => p.id === ingredient.formulation_phase_id);
                    if (phase && !aggregatedIngredients[key].phases_used.includes(phase.name)) {
                      aggregatedIngredients[key].phases_used.push(phase.name);
                    }
                    
                    // Get supplier info (prefer the first one found)
                    if (aggregatedIngredients[key].supplier_info === '—') {
                      let selectedSupplier = null;
                      try {
                        if (ingredient.notes) {
                          const notesData = JSON.parse(ingredient.notes);
                          if (notesData.selected_supplier) {
                            selectedSupplier = ingredientSuppliers.find(s => s.id === notesData.selected_supplier);
                          }
                        }
                      } catch (e) {
                        // Notes is not JSON, ignore
                      }
                      
                      if (selectedSupplier && selectedSupplier.suppliers) {
                        aggregatedIngredients[key].supplier_info = selectedSupplier.suppliers.name;
                      } else {
                        const materialSuppliers = ingredientSuppliers.filter(s => s.material_id === ingredient.ingredient_id);
                        if (materialSuppliers.length > 0) {
                          const preferredSupplier = materialSuppliers.find(s => s.is_preferred) || materialSuppliers[0];
                          if (preferredSupplier && preferredSupplier.suppliers) {
                            aggregatedIngredients[key].supplier_info = preferredSupplier.suppliers.name;
                          }
                        }
                      }
                    }
                  });
                  
                  return Object.values(aggregatedIngredients)
                    .sort((a, b) => a.ingredient_name.localeCompare(b.ingredient_name))
                    .map((item, index) => `
                      <tr>
                        <td>${index + 1}</td>
                        <td class="compact-cell">${item.ingredient_name}</td>
                        <td class="compact-cell">${item.supplier_info}</td>
                        <td class="compact-cell">${item.description}</td>
                        <td>${item.cas_number}</td>
                        <td class="percentage">${item.total_percentage.toFixed(2)}%</td>
                        <td class="percentage">${((item.total_percentage / 100) * formulation.target_batch_size).toFixed(3)} ${formulation.unit_of_measure}</td>
                        <td class="compact-cell">${item.phases_used.join(', ')}</td>
                      </tr>
                    `).join('');
                })()}
                <tr class="total-row">
                  <td style="font-weight: bold;"></td>
                  <td style="font-weight: bold;">Total:</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td class="percentage" style="font-weight: bold;">${totalPercentage.toFixed(2)}%</td>
                  <td class="percentage" style="font-weight: bold;">${formulation.target_batch_size} ${formulation.unit_of_measure}</td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div class="footer">
            Printed on: ${new Date().toLocaleString()} | Document is confidential
          </div>
        </body>
      </html>
    `;
    
    // Write to the iframe and ensure it's properly contained
    try {
      const frameDoc = printFrame.contentWindow.document;
      frameDoc.open();
      frameDoc.write(printContent);
      frameDoc.close();
      
      // Print when loaded
      printFrame.onload = function() {
        setTimeout(function() {
          try {
            printFrame.contentWindow.focus();
            printFrame.contentWindow.print();
            
            // Clean up after printing
            setTimeout(function() {
              try {
                if (printFrame && printFrame.parentNode) {
                  document.body.removeChild(printFrame);
                }
                document.title = originalTitle; // Restore original title
              } catch (cleanupErr) {
                console.error('Error cleaning up print frame:', cleanupErr);
              }
            }, 1000);
          } catch (err) {
            console.error('Print error:', err);
            // Clean up on error
            if (printFrame && printFrame.parentNode) {
              document.body.removeChild(printFrame);
            }
            document.title = originalTitle;
            showSnackbar('Print failed: ' + err.message, 'error');
          }
        }, 500);
      };
    } catch (frameErr) {
      console.error('Error creating print frame:', frameErr);
      // Clean up on error
      if (printFrame && printFrame.parentNode) {
        document.body.removeChild(printFrame);
      }
      document.title = originalTitle;
      showSnackbar('Print setup failed: ' + frameErr.message, 'error');
    }
  };
  
  const fetchIngredientsFromDb = async () => {
    try {
      const { data, error } = await supabase
        .from('raw_materials')
        .select('id, name, cas_number, description, sku, category, material_type, primary_unit_measure, color, form')
        .eq('company_id', formulation?.company_id)
        .eq('is_active', true)
        .order('name');
      
      if (error) throw error;
      
      setAvailableIngredients(data || []);
    } catch (err) {
      console.error('Error fetching raw materials:', err);
      showSnackbar('Error loading raw materials: ' + err.message, 'error');
    }
  };

  const fetchIngredientSuppliers = async (ingredientId) => {
    if (!ingredientId) return;
    
    setSupplierFetchLoading(true);
    try {
      // Get the specific raw material with all its supplier information
      const { data: materialData, error: materialError } = await supabase
        .from('raw_materials')
        .select(`
          id,
          name,
          supplier_id,
          unit_cost,
          primary_unit_measure,
          suppliers:supplier_id (
            id,
            name
          )
        `)
        .eq('id', ingredientId)
        .eq('is_active', true)
        .single();
      
      if (materialError) {
        console.error('Error fetching material data:', materialError);
        throw materialError;
      }
      
      if (materialData) {
        let suppliers = [];
        
        // Add primary supplier if it exists
        if (materialData.supplier_id && materialData.suppliers) {
          suppliers.push({
            id: `primary_${materialData.supplier_id}`,
            material_id: materialData.id,
            supplier_id: materialData.supplier_id,
            is_preferred: true,
            unit_cost: materialData.unit_cost,
            currency: 'USD',
            suppliers: materialData.suppliers
          });
        }
        
        setIngredientSuppliers(suppliers);
      }
    } catch (err) {
      console.error('Error fetching ingredient suppliers:', err);
      showSnackbar('Error loading suppliers: ' + err.message, 'error');
    } finally {
      setSupplierFetchLoading(false);
    }
  };

  const fetchIngredientSuppliersAndAutoSelect = async (ingredientId) => {
    if (!ingredientId) return;
    
    setSupplierFetchLoading(true);
    try {
      // Get the specific raw material with all its supplier information
      const { data: materialData, error: materialError } = await supabase
        .from('raw_materials')
        .select(`
          id,
          name,
          supplier_id,
          unit_cost,
          primary_unit_measure,
          suppliers:supplier_id (
            id,
            name
          )
        `)
        .eq('id', ingredientId)
        .eq('is_active', true)
        .single();
      
      if (materialError) {
        console.error('Error fetching material data:', materialError);
        throw materialError;
      }
      
      if (materialData) {
        let suppliers = [];
        let primarySupplierId = null;
        
        // Add primary supplier if it exists
        if (materialData.supplier_id && materialData.suppliers) {
          const supplierEntry = {
            id: `primary_${materialData.supplier_id}`,
            material_id: materialData.id,
            supplier_id: materialData.supplier_id,
            is_preferred: true,
            unit_cost: materialData.unit_cost,
            currency: 'USD',
            suppliers: materialData.suppliers
          };
          suppliers.push(supplierEntry);
          primarySupplierId = supplierEntry.id;
        }
        
        setIngredientSuppliers(suppliers);
        
        // Auto-select the primary supplier if it exists
        if (primarySupplierId) {
          setSelectedIngredient(prev => ({
            ...prev,
            supplier_link_id: primarySupplierId
          }));
        }
      }
    } catch (err) {
      console.error('Error fetching ingredient suppliers:', err);
      showSnackbar('Error loading suppliers: ' + err.message, 'error');
    } finally {
      setSupplierFetchLoading(false);
    }
  };

  const calculateIngredientAmount = (percentage, batchSize) => {
    return ((percentage / 100) * batchSize).toFixed(2);
  };

  // Calculate raw material amount needed using unitConversions.js WITH DENSITY
  const calculateIngredientAmountWithRawMaterialConversion = (percentage, batchSize, rawMaterial, formulation) => {
    const formulationAmount = (percentage / 100) * batchSize;
    const formulationUnit = formulation?.unit_of_measure || 'kg';
    const materialDensity = rawMaterial?.density_g_ml || 1.0;
    
    // Always show formulation amount
    const result = {
      formulationAmount: formulationAmount.toFixed(3),
      formulationUnit: formulationUnit,
      rawMaterialAmount: null,
      rawMaterialUnit: null,
      showRawMaterialConversion: false,
      conversionNote: null,
      density: materialDensity
    };

    // If no raw material data, return basic calculation
    if (!rawMaterial || !rawMaterial.primary_unit_measure) {
      return result;
    }

    const rawMaterialUnit = rawMaterial.primary_unit_measure;
    
    // ALWAYS show conversion if units are different - density is key!
    if (rawMaterialUnit !== formulationUnit) {
      try {
        // Use unitConversions.js WITH DENSITY for proper conversion
        const rawMaterialAmountNeeded = convertUnits(
          formulationAmount,
          formulationUnit,
          rawMaterialUnit,
          materialDensity  // THIS IS CRITICAL!
        );

        return {
          formulationAmount: formulationAmount.toFixed(3),
          formulationUnit: formulationUnit,
          rawMaterialAmount: rawMaterialAmountNeeded.toFixed(3),
          rawMaterialUnit: rawMaterialUnit,
          showRawMaterialConversion: true,
          conversionNote: `density: ${materialDensity.toFixed(3)} g/ml`,
          density: materialDensity
        };
      } catch (error) {
        console.warn(`Unit conversion failed for ${rawMaterial?.name}: ${error.message}`);
        return {
          ...result,
          conversionNote: `Conversion failed: ${error.message}`
        };
      }
    }

    // Same units - no conversion needed but still show density info
    return {
      ...result,
      conversionNote: materialDensity !== 1.0 ? `density: ${materialDensity.toFixed(3)} g/ml` : null
    };
  };

  // Keep old function name for compatibility, but use new logic
  const calculateIngredientAmountWithDensity = (percentage, batchSize, rawMaterial, formulation) => {
    const result = calculateIngredientAmountWithRawMaterialConversion(percentage, batchSize, rawMaterial, formulation);
    
    // Convert to old format for existing code
    return {
      baseAmount: result.formulationAmount,
      baseUnit: result.formulationUnit,
      densityAmount: result.rawMaterialAmount,
      densityUnit: result.rawMaterialUnit,
      showDensity: result.showRawMaterialConversion,
      conversionNote: result.conversionNote
    };
  };

  const handleAddPhase = () => {
    setPhaseDialogMode('create');
    setSelectedPhaseData({
      name: '',
      description: '',
      sequence: phases.length + 1,
      formulation_id: formulation?.id
    });
    setPhaseDialogOpen(true);
  };

  const handleEditPhase = (phase) => {
    setPhaseDialogMode('edit');
    setSelectedPhaseData({
      ...phase
    });
    setPhaseDialogOpen(true);
  };

  const handlePhaseChange = (e) => {
    const { name, value } = e.target;
    setSelectedPhaseData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSavePhase = async () => {
    try {
      setLoading(true);
      
      if (!selectedPhaseData.name) {
        showSnackbar('Please enter a phase name', 'error');
        setLoading(false);
        return;
      }

      if (phaseDialogMode === 'create') {
        const { data, error } = await supabase
          .from('formulation_phases')
          .insert([{
            ...selectedPhaseData,
            formulation_id: formulation.id
          }])
          .select()
          .single();
          
        if (error) throw error;
        
        showSnackbar('Phase added successfully', 'success');
        // Update phases in parent component
        phases.push(data);
      } else {
        const { data, error } = await supabase
          .from('formulation_phases')
          .update(selectedPhaseData)
          .eq('id', selectedPhaseData.id)
          .select()
          .single();
          
        if (error) throw error;
        
        // Update phase in the array
        const phaseIndex = phases.findIndex(p => p.id === data.id);
        if (phaseIndex !== -1) {
          phases[phaseIndex] = data;
        }
        
        showSnackbar('Phase updated successfully', 'success');
      }
      
      setPhaseDialogOpen(false);
    } catch (err) {
      showSnackbar('Error: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteConfirmation = (phaseId) => {
    setDeletePhaseId(phaseId);
    setDeleteConfirmationOpen(true);
  };

  const handleDeletePhase = async () => {
    try {
      setLoading(true);
      
      const phaseIngredients = formulationIngredients.filter(
        ing => ing.formulation_phase_id === deletePhaseId
      );
      
      if (phaseIngredients.length > 0) {
        // Delete all ingredients in this phase first
        const { error: ingredientsError } = await supabase
          .from('formulation_ingredients_new')
          .delete()
          .eq('formulation_phase_id', deletePhaseId);
          
        if (ingredientsError) throw ingredientsError;
      }
      
      // Now delete the phase
      const { error } = await supabase
        .from('formulation_phases')
        .delete()
        .eq('id', deletePhaseId);
        
      if (error) throw error;
      
      // Update phases in parent
      const phaseIndex = phases.findIndex(p => p.id === deletePhaseId);
      if (phaseIndex !== -1) {
        phases.splice(phaseIndex, 1);
      }
      
      showSnackbar('Phase deleted successfully', 'success');
      setDeleteConfirmationOpen(false);
    } catch (err) {
      showSnackbar('Error: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAddIngredient = (phaseId) => {
    setIngredientDialogMode('create');
    setSelectedPhase(phaseId);
    setSelectedIngredient({
      ingredient_id: '',
      percentage: '',
      amount: '',
      amount_unit: formulation?.unit_of_measure || 'g',
      formulation_phase_id: phaseId,
      function: '',
      sequence: 0,
      notes: '',
      supplier_link_id: null
    });
    
    setIngredientDialogOpen(true);
  };

  const handleEditIngredient = (ingredient) => {
    setIngredientDialogMode('edit');
    setSelectedPhase(ingredient.formulation_phase_id);
    
    // Extract user notes and supplier selection from JSON notes
    let userNotes = ingredient.notes || '';
    let supplierLinkId = '';
    
    try {
      if (ingredient.notes) {
        const notesData = JSON.parse(ingredient.notes);
        userNotes = notesData.user_notes || '';
        supplierLinkId = notesData.selected_supplier || '';
      }
    } catch (e) {
      // Notes is not JSON, use as user notes
      userNotes = ingredient.notes || '';
    }
    
    setSelectedIngredient({
      ...ingredient,
      amount: ingredient.amount || '',
      amount_unit: ingredient.amount_unit || formulation?.unit_of_measure || 'g',
      function: ingredient.function || '',
      notes: userNotes,
      supplier_link_id: supplierLinkId
    });
    
    // Fetch suppliers for this ingredient
    fetchIngredientSuppliers(ingredient.ingredient_id);
    
    setIngredientDialogOpen(true);
  };

  const handleIngredientChange = (e) => {
    const { name, value } = e.target;
    setSelectedIngredient(prev => ({
      ...prev,
      [name]: value
    }));
    
    // If the ingredient was changed, fetch suppliers for that ingredient and auto-select primary supplier
    if (name === 'ingredient_id' && value) {
      // Clear any previously selected supplier initially
      setSelectedIngredient(prev => ({
        ...prev,
        supplier_link_id: null
      }));
      
      // Fetch suppliers for this ingredient and auto-select the primary one
      fetchIngredientSuppliersAndAutoSelect(value);
    }
  };

  const handleSaveIngredient = async () => {
    try {
      setLoading(true);
      
      // Validate required fields
      if (!selectedIngredient?.ingredient_id) {
        showSnackbar('Please select an ingredient', 'error');
        setLoading(false);
        return; // Don't close dialog
      }
      
      if (!selectedIngredient?.percentage || isNaN(parseFloat(selectedIngredient.percentage))) {
        showSnackbar('Please enter a valid percentage', 'error');
        setLoading(false);
        return; // Don't close dialog
      }
      
      // Calculate sequence value if not set
      const phaseIngredients = formulationIngredients.filter(
        ing => ing.formulation_phase_id === selectedIngredient.formulation_phase_id
      );
      
      const nextSequence = selectedIngredient.sequence || 
        (phaseIngredients.length > 0 
          ? Math.max(...phaseIngredients.map(ing => ing.sequence || 0)) + 1 
          : 0);

      // Process numeric values
      const processedIngredient = {
        ...selectedIngredient,
        sequence: nextSequence,
        formulation_id: formulation.id,
        percentage: parseFloat(selectedIngredient.percentage),
        amount: selectedIngredient.amount ? parseFloat(selectedIngredient.amount) : null,
        supplier_link_id: null,
        notes: selectedIngredient.supplier_link_id ? JSON.stringify({
          user_notes: selectedIngredient.notes || '',
          selected_supplier: selectedIngredient.supplier_link_id
        }) : selectedIngredient.notes || ''
      };

      if (ingredientDialogMode === 'create') {
        // Insert ingredient
        const { data, error } = await supabase
          .from('formulation_ingredients_new')
          .insert([processedIngredient])
          .select()
          .single();
        
        if (error) {
          console.error('Database error adding ingredient:', error);
          throw new Error(error.message || 'Error adding ingredient');
        }
        
        // Add to local state
        setFormulationIngredients(prev => [...prev, data]);
        showSnackbar('Ingredient added successfully', 'success');
        setIngredientDialogOpen(false);
      } else {
        // Update existing ingredient
        const { data, error } = await supabase
          .from('formulation_ingredients_new')
          .update({
            ingredient_id: processedIngredient.ingredient_id,
            percentage: processedIngredient.percentage,
            amount: processedIngredient.amount,
            amount_unit: processedIngredient.amount_unit,
            function: processedIngredient.function,
            notes: processedIngredient.notes
          })
          .eq('id', selectedIngredient.id)
          .select()
          .single();
          
        if (error) {
          console.error('Database error updating ingredient:', error);
          throw new Error(error.message || 'Error updating ingredient');
        }
        
        // Update local state properly with the state setter
        setFormulationIngredients(prev => 
          prev.map(ing => ing.id === data.id ? data : ing)
        );
        
        showSnackbar('Ingredient updated successfully', 'success');
        setIngredientDialogOpen(false);
      }
    } catch (err) {
      showSnackbar('Error: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const openDeleteIngredientConfirmation = (ingredientId) => {
    setDeleteIngredientId(ingredientId);
    setDeleteIngredientConfirmOpen(true);
  };

  const handleDeleteIngredient = async () => {
    if (!deleteIngredientId) return;
    
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('formulation_ingredients_new')
        .delete()
        .eq('id', deleteIngredientId);
        
      if (error) throw error;
      
      // Update local state properly with the state setter
      setFormulationIngredients(prev => prev.filter(ing => ing.id !== deleteIngredientId));
      
      showSnackbar('Ingredient deleted successfully', 'success');
      setDeleteIngredientConfirmOpen(false);
    } catch (err) {
      showSnackbar('Error: ' + err.message, 'error');
    } finally {
      setLoading(false);
      setDeleteIngredientId(null);
    }
  };

  const handleNewIngredientChange = (e) => {
    const { name, value } = e.target;
    setNewIngredientData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCreateNewIngredient = async () => {
    try {
      setLoading(true);
      
      // Validate
      if (!newIngredientData.name) {
        showSnackbar('Ingredient name is required', 'error');
        setLoading(false);
        return;
      }
      
      
      // Create new ingredient in the database as a raw material
      const { data, error } = await supabase
        .from('raw_materials')
        .insert([{
          name: newIngredientData.name,
          cas_number: newIngredientData.cas_number || null,
          description: newIngredientData.description || newIngredientData.inci_name || null,
          primary_unit_type: 'weight',
          primary_unit_quantity: 1,
          primary_unit_measure: 'g',
          company_id: formulation.company_id,
          is_active: true
        }])
        .select()
        .single();
        
      if (error) throw error;
      
      // Add to available ingredients
      setAvailableIngredients(prev => [...prev, data]);
      
      // Select the new ingredient
      setSelectedIngredient(prev => ({
        ...prev,
        ingredient_id: data.id
      }));
      
      showSnackbar('New raw material created successfully', 'success');
      setNewIngredientDialogOpen(false);
    } catch (err) {
      console.error('Error creating raw material:', err);
      showSnackbar('Error: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch formulation ingredients when component loads
  useEffect(() => {
    const fetchIngredients = async () => {
      try {
        if (!formulation?.id) return;
        
        const { data, error } = await supabase
          .from('formulation_ingredients_new')
          .select(`
            id,
            formulation_id,
            formulation_phase_id,
            ingredient_id,
            percentage,
            amount,
            amount_unit,
            sequence,
            function,
            notes,
            supplier_link_id,
            raw_materials:ingredient_id (id, name, cas_number, description, sku, category, material_type, primary_unit_measure, color, form, density_g_ml)
          `)
          .eq('formulation_id', formulation.id)
          .order('sequence');
          
        if (error) throw error;
        
        setFormulationIngredients(data || []);
        
        // After loading ingredients, fetch all related suppliers
        fetchAllIngredientSuppliers(data || []);
      } catch (err) {
        console.error('Error fetching formulation ingredients:', err);
        showSnackbar('Error loading formulation ingredients: ' + err.message, 'error');
      }
    };
    
    fetchIngredients();
  }, [formulation, showSnackbar]);
  
  // Fetch all suppliers for all raw materials in the formulation
  const fetchAllIngredientSuppliers = async (ingredients = formulationIngredients) => {
    try {
      if (!formulation?.company_id) {
        return;
      }
      
      if (ingredients && ingredients.length > 0) {
        const ingredientIds = [...new Set(ingredients.map(item => item.ingredient_id))];
        
        // Get raw materials data with their supplier information
        const { data: materialsData, error: materialsError } = await supabase
          .from('raw_materials')
          .select(`
            id,
            name,
            supplier_id,
            unit_cost,
            primary_unit_measure,
            density_g_ml,
            suppliers:supplier_id (
              id,
              name
            )
          `)
          .in('id', ingredientIds)
          .eq('is_active', true);
        
        if (materialsError) {
          console.error('Error fetching materials data:', materialsError);
          throw materialsError;
        }
        
        // Build suppliers list from materials data
        let allSuppliers = [];
        
        for (const material of materialsData || []) {
          // Add preferred supplier from direct reference
          if (material.supplier_id && material.suppliers) {
            allSuppliers.push({
              id: `direct_${material.supplier_id}`,
              material_id: material.id,
              supplier_id: material.supplier_id,
              is_preferred: true,
              unit_cost: material.unit_cost,
              currency: 'USD',
              suppliers: material.suppliers
            });
          }
        }
        
        setIngredientSuppliers(allSuppliers);
      }
    } catch (err) {
      console.error('Error fetching ingredient suppliers:', err);
      showSnackbar('Error loading supplier data: ' + err.message, 'error');
    }
  };



  // Calculate total percentage
  const totalPercentage = formulationIngredients.reduce((sum, item) => sum + parseFloat(item.percentage || 0), 0);
  
  // Auto-check ingredients completion when total is 100%
  useEffect(() => {
    const checkIngredientsCompletion = async () => {
      if (!formulation?.id || !user?.id) return;
      
      const isComplete = formulationIngredients.length > 0 && Math.abs(totalPercentage - 100) < 0.01;
      
      // Update completion status if changed
      if (isComplete !== formulation?.ingredients_complete) {
        try {
          const { error } = await supabase
            .from('formulations')
            .update({
              ingredients_complete: isComplete,
              ingredients_completed_at: isComplete ? new Date().toISOString() : null,
              ingredients_completed_by: isComplete ? user.id : null
            })
            .eq('id', formulation.id);
            
          if (error) {
            console.error('Error updating ingredients completion:', error);
          } else if (onUpdate) {
            onUpdate();
          }
        } catch (err) {
          console.error('Error updating ingredients completion:', err);
        }
      }
    };
    
    checkIngredientsCompletion();
  }, [totalPercentage, formulationIngredients.length, formulation?.id, formulation?.ingredients_complete, user?.id, onUpdate]);

  // Check permissions for ingredients tab specifically
  const canViewIngredients = hasPermission('products.formulations.ingredients.view', 'read');
  const canEditIngredients = hasPermission('products.formulations.ingredients.edit', 'write');
  const canDeleteIngredients = hasPermission('products.formulations.ingredients.delete', 'delete');

  // Permission check
  if (!canViewIngredients) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        minHeight: '60vh',
        textAlign: 'center',
        px: 3
      }}>
        <Card sx={{
          p: 6,
          borderRadius: 4,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: 'white',
          boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
          maxWidth: 500,
          width: '100%',
          position: 'relative',
          overflow: 'hidden',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, transparent)`,
            zIndex: 0,
            opacity: 0.8,
            transition: 'opacity 0.3s ease'
          },
          '&:hover::before': {
            opacity: 1
          }
        }}>
          <Box sx={{ 
            p: 3, 
            borderRadius: 3, 
            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            mb: 6,
            display: 'inline-flex',
            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
          }}>
            <ScienceIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
          </Box>
          <Typography variant="h3" sx={{ 
            mb: 3, 
            fontWeight: 500, 
            color: 'text.primary',
            fontSize: '2.5rem',
            letterSpacing: '-0.8px'
          }}>
            Access Restricted
          </Typography>
          <Typography variant="h6" sx={{ 
            color: 'text.secondary', 
            mb: 4,
            fontWeight: 500,
            fontSize: '1.2rem'
          }}>
            Formulation Ingredients Required
          </Typography>
          <Typography variant="body1" sx={{ 
            color: 'text.secondary',
            lineHeight: 1.7,
            mb: 6,
            fontSize: '1.05rem',
            fontWeight: 500
          }}>
            You don't currently have the necessary permissions to access the Formulation Ingredients. Contact your system administrator to request access or verify your role permissions.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box 
      sx={{ 
        pb: 4,
        display: 'flex',
        justifyContent: 'center',
        width: '100%'
      }}
    >
      <Box sx={{ maxWidth: '100vw', width: '100%', px: { xs: 2, sm: 3 } }}>
      {/* Enhanced Header Section */}
      <Card 
        sx={{ 
          borderRadius: 3,
          mb: 2,
          border: `1px solid ${theme.palette.grey[400]}`,
          background: 'white',
          backdropFilter: 'blur(20px)',
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box sx={{ flex: 1 }}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <Box sx={{ 
                  p: 1.5, 
                  borderRadius: 2, 
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                  backdropFilter: 'blur(10px)'
                }}>
                  <ScienceIcon sx={{ fontSize: 24, color: theme.palette.primary.main }} />
                </Box>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                    Ingredients Management
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Organize ingredients by phases and track precise measurements for your formulation
                  </Typography>
                </Box>
              </Stack>
            
            {/* Quick Stats */}
            <Stack direction="row" spacing={3} sx={{ mt: 3 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                  {phases.length}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Phases
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ fontWeight: 500, color: theme.palette.primary.main }}>
                  {formulationIngredients.length}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Ingredients
                </Typography>
              </Box>
              <Divider orientation="vertical" flexItem />
              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 500, 
                  color: totalPercentage === 100 ? 'success.main' : totalPercentage > 100 ? 'error.main' : 'warning.main'
                }}>
                  {totalPercentage.toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Total
                </Typography>
              </Box>
            </Stack>
          </Box>
          
          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<PrintIcon />}
              onClick={handlePrintIngredientList}
              disabled={formulationIngredients.length === 0}
              sx={{ 
                borderRadius: 3,
                px: 3,
                py: 1.5,
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: theme.palette.primary.main,
                fontWeight: 500,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                },
                '&:disabled': {
                  opacity: 0.5
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              Print List
            </Button>
            {phases.length === 0 && canEditIngredients && (
              <Button 
                variant="contained" 
                startIcon={<Add />}
                onClick={async () => {
                  try {
                    const defaultPhase = {
                      name: 'Phase A',
                      description: 'Default phase',
                      sequence: 1,
                      formulation_id: formulation.id
                    };
                    
                    const { data, error } = await supabase
                      .from('formulation_phases')
                      .insert([defaultPhase])
                      .select()
                      .single();
                      
                    if (error) throw error;
                    
                    phases.push(data);
                    handleAddIngredient(data.id);
                    showSnackbar('Default phase created. Now add your ingredients.', 'success');
                  } catch (err) {
                    showSnackbar('Error creating default phase: ' + err.message, 'error');
                  }
                }}
                sx={{ 
                  borderRadius: 3,
                  px: 3,
                  py: 1.5,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  fontWeight: 500,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                  },
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Add Ingredients
              </Button>
            )}
            {canEditIngredients && (
              <Button 
                variant="outlined" 
                startIcon={<Add />}
                onClick={handleAddPhase}
              sx={{ 
                borderRadius: 3,
                px: 3,
                py: 1.5,
                borderColor: alpha(theme.palette.primary.main, 0.3),
                color: theme.palette.primary.main,
                fontWeight: 500,
                background: 'rgba(255, 255, 255, 0.8)',
                backdropFilter: 'blur(10px)',
                '&:hover': {
                  borderColor: theme.palette.primary.main,
                  backgroundColor: alpha(theme.palette.primary.main, 0.04),
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                },
                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              >
                Add Phase
              </Button>
            )}
          </Stack>
        </Box>
      </CardContent>
    </Card>
      
      {phases.length === 0 ? (
        <Paper 
          elevation={0}
          sx={{ 
            p: 6, 
            textAlign: 'center',
            borderRadius: 4,
            border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.03)}, ${alpha(theme.palette.primary.main, 0.01)})`
          }}
        >
          <Box sx={{ 
            p: 3,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
            display: 'inline-block',
            mb: 3
          }}>
            <ScienceIcon sx={{ fontSize: 64, color: 'primary.main' }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 500, mb: 2, color: 'text.primary' }}>
            No Phases Created Yet
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 500, mx: 'auto' }}>
            Get started by adding ingredients to quickly create your first phase, or organize your formulation with custom phases.
          </Typography>
          <Stack direction="row" spacing={3} justifyContent="center">
            {canEditIngredients && (
              <Button 
                variant="contained"
                startIcon={<Add />}
                size="large"
                onClick={async () => {
                  try {
                    const defaultPhase = {
                      name: 'Phase A',
                      description: 'Default phase',
                      sequence: 1,
                      formulation_id: formulation.id
                    };
                    
                    const { data, error } = await supabase
                      .from('formulation_phases')
                      .insert([defaultPhase])
                      .select()
                      .single();
                      
                    if (error) throw error;
                    
                    phases.push(data);
                    handleAddIngredient(data.id);
                    showSnackbar('Default phase created. Now add your ingredients.', 'success');
                  } catch (err) {
                    showSnackbar('Error creating default phase: ' + err.message, 'error');
                  }
                }}
                sx={{ 
                  borderRadius: 3,
                  px: 4,
                  py: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                  fontSize: '1rem',
                  fontWeight: 500,
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Quick Start - Add Ingredients
              </Button>
            )}
            {canEditIngredients && (
              <Button 
                variant="outlined"
                startIcon={<Add />}
                size="large"
                onClick={handleAddPhase}
                sx={{ 
                  borderRadius: 3,
                  px: 4,
                  py: 2,
                  borderWidth: 1,
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: theme.palette.primary.main,
                  fontSize: '1rem',
                  fontWeight: 500,
                  background: 'rgba(255, 255, 255, 0.8)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04),
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
              >
                Create Custom Phase
              </Button>
            )}

          </Stack>
        </Paper>
      ) : (
        <Box sx={{ mb: 4 }}>
          {phases.map((phase, phaseIndex) => (
            <Accordion 
              key={phase.id} 
              expanded={expandedPhases[phase.id] || false}
              elevation={0}
              sx={{
                mb: 2,
                borderRadius: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                backdropFilter: 'blur(20px)',
                overflow: 'hidden',
                boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`,
                '&:before': {
                  display: 'none'
                },
                '&.Mui-expanded': {
                  margin: '0 0 16px 0'
                },
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
              onChange={handleAccordionChange(phase.id)}
            >
              <AccordionSummary 
                expandIcon={<ExpandMore sx={{ color: theme.palette.primary.main }} />}
                sx={{
                  borderRadius: '12px 12px 0 0',
                  background: 'white',
                  backdropFilter: 'blur(10px)',
                  border: 'none',
                  minHeight: 72,
                  '&.Mui-expanded': {
                    minHeight: 72,
                    borderRadius: '12px 12px 0 0'
                  },
                  '& .MuiAccordionSummary-content': {
                    margin: '16px 0',
                    alignItems: 'center',
                    '&.Mui-expanded': {
                      margin: '16px 0'
                    }
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                  <Box sx={{ 
                    p: 1.5, 
                    borderRadius: 2, 
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                    color: theme.palette.primary.main,
                    mr: 3
                  }}>
                    <WidgetsIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                      {phase.name}
                    </Typography>
                    {phase.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                        {phase.description}
                      </Typography>
                    )}
                  </Box>
                  <Chip 
                    label={`${formulationIngredients.filter(fi => fi.formulation_phase_id === phase.id).length} ingredients`}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      fontWeight: 500,
                      borderColor: theme.palette.primary.main,
                      color: theme.palette.primary.main,
                      background: 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(10px)',
                      mr: 2
                    }}
                  />
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Stack direction="row" spacing={2}>
                    {canEditIngredients && (
                      <Button 
                        size="small"
                        startIcon={<Edit />}
                        onClick={() => handleEditPhase(phase)}
                        sx={{ 
                          borderRadius: 3,
                          px: 3,
                          py: 1.5,
                          background: 'white',
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          color: theme.palette.primary.main,
                          fontWeight: 500,
                          backdropFilter: 'blur(10px)',
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.1)}`,
                          '&:hover': {
                            background: 'white',
                            borderColor: theme.palette.primary.main,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.25)}`
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                        variant="outlined"
                      >
                        Edit Phase
                      </Button>
                    )}
                    {canDeleteIngredients && (
                      <Button 
                        size="small"
                        startIcon={<Delete />}
                        onClick={() => openDeleteConfirmation(phase.id)}
                        color="error"
                        variant="outlined"
                        sx={{ 
                          borderRadius: 3,
                          px: 3,
                          py: 1.5,
                          background: 'white',
                          border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                          color: theme.palette.error.main,
                          fontWeight: 500,
                          backdropFilter: 'blur(10px)',
                          boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.1)}`,
                          '&:hover': {
                            background: 'white',
                            borderColor: theme.palette.error.main,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 6px 20px ${alpha(theme.palette.error.main, 0.25)}`
                          },
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}
                      >
                        Delete Phase
                      </Button>
                    )}
                  </Stack>
                  {canEditIngredients && (
                    <Button 
                      size="small"
                      startIcon={<Add />}
                      onClick={() => handleAddIngredient(phase.id)}
                      variant="contained"
                      sx={{ 
                        borderRadius: 2,
                        px: 3,
                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                        color: 'white',
                        fontWeight: 500,
                        boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                        '&:hover': {
                          background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                          transform: 'translateY(-1px)',
                          boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
                        },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}
                    >
                      Add Ingredient
                    </Button>
                  )}
                </Box>
                
                <TableContainer 
                  sx={{ 
                    borderRadius: 3,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                    boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                    overflow: 'hidden',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                    }
                  }}
                >
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          Ingredient
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          Supplier
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          Description
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          CAS Number
                        </TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          %
                        </TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          Amount
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          Function
                        </TableCell>
                        <TableCell 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          Notes
                        </TableCell>
                        <TableCell 
                          align="right" 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `1px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3,
                            width: 160
                          }}
                        >
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formulationIngredients
                        .filter(fi => fi.formulation_phase_id === phase.id)
                        .map(ingredient => {
                          // Get raw material info from the nested structure or fallback to availableIngredients
                          const ingredientInfo = ingredient.raw_materials || availableIngredients.find(i => i.id === ingredient.ingredient_id);
                          
                          return (
                            <TableRow 
                              key={ingredient.id} 
                              hover 
                              sx={{ 
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                '&:hover': { 
                                  bgcolor: 'grey.50',
                                  transform: 'translateX(4px)',
                                  transition: 'all 0.2s ease',
                                  boxShadow: 'inset 4px 0 0 black'
                                },
                                '& .MuiTableCell-root': {
                                  borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                  py: 2.5,
                                  px: 3
                                }
                              }}
                            >
                              <TableCell sx={{ fontWeight: 500 }}>
                                {ingredientInfo?.name || 'Unknown Ingredient'}
                              </TableCell>
                              <TableCell>
                                {(() => {
                                  // Try to extract supplier from notes first
                                  let selectedSupplier = null;
                                  try {
                                    if (ingredient.notes) {
                                      const notesData = JSON.parse(ingredient.notes);
                                      if (notesData.selected_supplier) {
                                        // Find the selected supplier in our suppliers list
                                        selectedSupplier = ingredientSuppliers.find(s => s.id === notesData.selected_supplier);
                                      }
                                    }
                                  } catch (e) {
                                    // Notes is not JSON, ignore
                                  }
                                  
                                  // Show selected supplier if found
                                  if (selectedSupplier && selectedSupplier.suppliers) {
                                    return (
                                      <Box sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: 2,
                                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        color: theme.palette.primary.main,
                                        fontSize: '0.875rem',
                                        fontWeight: 500
                                      }}>
                                        {selectedSupplier.suppliers.name}
                                      </Box>
                                    );
                                  }
                                  
                                  // Fall back to the preferred supplier for this material
                                  const materialSuppliers = ingredientSuppliers.filter(s => s.material_id === ingredient.ingredient_id);
                                  if (materialSuppliers.length > 0) {
                                    const preferredSupplier = materialSuppliers.find(s => s.is_preferred) || materialSuppliers[0];
                                    if (preferredSupplier && preferredSupplier.suppliers) {
                                      return (
                                        <Box sx={{
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          px: 2,
                                          py: 0.5,
                                          borderRadius: 2,
                                          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
                                          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                          color: theme.palette.primary.main,
                                          fontSize: '0.875rem',
                                          fontWeight: 500
                                        }}>
                                          {preferredSupplier.suppliers.name}
                                        </Box>
                                      );
                                    }
                                  }
                                  
                                  return (
                                    <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', fontWeight: 500 }}>
                                      No supplier
                                    </Typography>
                                  );
                                })()}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {ingredientInfo?.description || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                  {ingredientInfo?.cas_number || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                                  {ingredient.percentage}%
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                {(() => {
                                  const amountCalc = calculateIngredientAmountWithRawMaterialConversion(
                                    ingredient.percentage,
                                    formulation.target_batch_size,
                                    ingredientInfo,
                                    formulation
                                  );
                                  
                                  return (
                                    <>
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        {amountCalc.formulationAmount} {amountCalc.formulationUnit}
                                      </Typography>
                                      {amountCalc.showRawMaterialConversion && (
                                        <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}>
                                          ≈{amountCalc.rawMaterialAmount} {amountCalc.rawMaterialUnit} raw
                                        </Typography>
                                      )}
                                      {amountCalc.conversionNote && (
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                          {amountCalc.conversionNote}
                                        </Typography>
                                      )}
                                    </>
                                  );
                                })()}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {ingredient.function || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {(() => {
                                    try {
                                      if (ingredient.notes) {
                                        const notesData = JSON.parse(ingredient.notes);
                                        return notesData.user_notes || '—';
                                      }
                                    } catch (e) {
                                      return ingredient.notes || '—';
                                    }
                                    return '—';
                                  })()}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                  {canEditIngredients && (
                                    <IconButton 
                                      size="small"
                                      onClick={() => handleEditIngredient(ingredient)}
                                      sx={{
                                        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
                                        border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                                        color: theme.palette.primary.main,
                                        '&:hover': {
                                          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.12)}, ${alpha(theme.palette.primary.main, 0.08)})`,
                                          transform: 'scale(1.1)',
                                          boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                                        },
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                      }}
                                    >
                                      <Edit fontSize="small" />
                                    </IconButton>
                                  )}
                                  {canDeleteIngredients && (
                                    <IconButton 
                                      size="small" 
                                      onClick={() => openDeleteIngredientConfirmation(ingredient.id)}
                                      sx={{
                                        background: alpha(theme.palette.error.main, 0.08),
                                        border: `1px solid ${alpha(theme.palette.error.main, 0.2)}`,
                                        color: 'error.main',
                                        '&:hover': {
                                          background: alpha(theme.palette.error.main, 0.12),
                                          transform: 'scale(1.1)',
                                          boxShadow: `0 4px 12px ${alpha(theme.palette.error.main, 0.2)}`
                                        },
                                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                      }}
                                    >
                                      <Delete fontSize="small" />
                                    </IconButton>
                                  )}
                                </Stack>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      }
                      {formulationIngredients.filter(fi => fi.formulation_phase_id === phase.id).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={9} align="center" sx={{ py: 6 }}>
                            <Box sx={{ textAlign: 'center' }}>
                              <ScienceIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 500, mb: 1 }}>
                                No ingredients in this phase
                              </Typography>
                              <Typography variant="body2" color="text.disabled">
                                Click "Add Ingredient" to start building your formulation
                              </Typography>
                            </Box>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Enhanced Total Usage Summary Section */}
      {formulationIngredients.length > 0 && (
        <Paper 
          elevation={0}
          sx={{ 
            mt: 2, 
            p: 2,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.02)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`
          }}
        >
          <Box sx={{ 
            mb: 2, 
            p: 1.5, 
            borderRadius: 2, 
            background: 'white',
            border: `1px solid ${theme.palette.grey[300]}`
          }}>
            <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1.5 }}>
              <Box sx={{ 
                p: 1.5, 
                borderRadius: 2, 
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.main, 0.05)})`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                color: theme.palette.primary.main
              }}>
                <ScienceIcon sx={{ fontSize: 24 }} />
              </Box>
              <Box>
                <Typography variant="h5" sx={{ fontWeight: 500, color: theme.palette.text.primary, mb: 0.5 }}>
                  Total Usage Summary
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Aggregated ingredient usage across all phases for accurate cost calculation and procurement
                </Typography>
              </Box>
            </Stack>
          </Box>
          
          <TableContainer 
            sx={{ 
              borderRadius: 3,
              border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
              background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
              boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
              overflow: 'hidden',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                transform: 'translateY(-2px)',
                boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
              }
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `1px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Ingredient
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `1px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Supplier
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `1px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Description
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `1px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    CAS Number
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `1px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Total %
                  </TableCell>
                  <TableCell 
                    align="right" 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `1px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Total Amount
                  </TableCell>
                  <TableCell 
                    sx={{ 
                      fontWeight: 500,
                      fontSize: '0.9rem',
                      bgcolor: 'white',
                      borderBottom: `1px solid ${theme.palette.primary.main}`,
                      color: 'primary.main',
                      py: 2,
                      px: 3
                    }}
                  >
                    Phases Used
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  // Aggregate ingredients by ingredient_id
                  const aggregatedIngredients = {};
                  
                  formulationIngredients.forEach(ingredient => {
                    const key = ingredient.ingredient_id;
                    if (!aggregatedIngredients[key]) {
                      const ingredientInfo = ingredient.raw_materials || availableIngredients.find(i => i.id === ingredient.ingredient_id);
                      aggregatedIngredients[key] = {
                        ingredient_id: ingredient.ingredient_id,
                        ingredient_name: ingredientInfo?.name || 'Unknown Ingredient',
                        description: ingredientInfo?.description || '—',
                        cas_number: ingredientInfo?.cas_number || '—',
                        total_percentage: 0,
                        phases_used: [],
                        supplier_info: null
                      };
                    }
                    
                    aggregatedIngredients[key].total_percentage += parseFloat(ingredient.percentage || 0);
                    
                    // Add phase name to the list
                    const phase = phases.find(p => p.id === ingredient.formulation_phase_id);
                    if (phase && !aggregatedIngredients[key].phases_used.includes(phase.name)) {
                      aggregatedIngredients[key].phases_used.push(phase.name);
                    }
                    
                    // Get supplier info (prefer the first one found)
                    if (!aggregatedIngredients[key].supplier_info) {
                      let selectedSupplier = null;
                      try {
                        if (ingredient.notes) {
                          const notesData = JSON.parse(ingredient.notes);
                          if (notesData.selected_supplier) {
                            selectedSupplier = ingredientSuppliers.find(s => s.id === notesData.selected_supplier);
                          }
                        }
                      } catch (e) {
                        // Notes is not JSON, ignore
                      }
                      
                      if (selectedSupplier && selectedSupplier.suppliers) {
                        aggregatedIngredients[key].supplier_info = selectedSupplier.suppliers.name;
                      } else {
                        const materialSuppliers = ingredientSuppliers.filter(s => s.material_id === ingredient.ingredient_id);
                        if (materialSuppliers.length > 0) {
                          const preferredSupplier = materialSuppliers.find(s => s.is_preferred) || materialSuppliers[0];
                          if (preferredSupplier && preferredSupplier.suppliers) {
                            aggregatedIngredients[key].supplier_info = preferredSupplier.suppliers.name;
                          }
                        }
                      }
                    }
                  });
                  
                  return Object.values(aggregatedIngredients)
                    .sort((a, b) => a.ingredient_name.localeCompare(b.ingredient_name))
                    .map((item, index) => (
                      <TableRow 
                        key={item.ingredient_id} 
                        hover
                        sx={{
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                          '&:hover': {
                            bgcolor: 'grey.50',
                            transform: 'translateX(4px)',
                            transition: 'all 0.2s ease',
                            boxShadow: 'inset 4px 0 0 black'
                          },
                          '& .MuiTableCell-root': {
                            borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                            py: 2.5,
                            px: 3
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {item.ingredient_name}
                        </TableCell>
                        <TableCell>
                          {item.supplier_info ? (
                            <Box sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              px: 2,
                              py: 0.5,
                              borderRadius: 2,
                              background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
                              border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                              color: theme.palette.primary.main,
                              fontSize: '0.875rem',
                              fontWeight: 500
                            }}>
                              {item.supplier_info}
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled" sx={{ fontStyle: 'italic', fontWeight: 500 }}>
                              No supplier
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {item.description}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                            {item.cas_number}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                            {item.total_percentage.toFixed(2)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {(() => {
                            const ingredientInfo = availableIngredients.find(ing => ing.id === item.ingredient_id);
                            const amountCalc = calculateIngredientAmountWithRawMaterialConversion(
                              item.total_percentage,
                              formulation.target_batch_size,
                              ingredientInfo,
                              formulation
                            );
                            
                            return (
                              <>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {amountCalc.formulationAmount} {amountCalc.formulationUnit}
                                </Typography>
                                {amountCalc.showRawMaterialConversion && (
                                  <Typography variant="caption" color="primary.main" sx={{ display: 'block', mt: 0.5, fontWeight: 500 }}>
                                    ≈{amountCalc.rawMaterialAmount} {amountCalc.rawMaterialUnit} raw
                                  </Typography>
                                )}
                                {amountCalc.conversionNote && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, fontSize: '0.7rem' }}>
                                    {amountCalc.conversionNote}
                                  </Typography>
                                )}
                              </>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} flexWrap="wrap">
                            {item.phases_used.map((phaseName, idx) => (
                              <Chip
                                key={idx}
                                label={phaseName}
                                size="small"
                                variant="outlined"
                                sx={{ 
                                  fontSize: '0.75rem',
                                  borderRadius: 2,
                                  borderColor: theme.palette.primary.main,
                                  color: theme.palette.primary.main,
                                  background: 'rgba(255, 255, 255, 0.8)',
                                  backdropFilter: 'blur(10px)'
                                }}
                              />
                            ))}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ));
                })()}
                <TableRow sx={{ 
                  borderTop: `1px solid ${theme.palette.primary.main}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
                }}>
                  <TableCell>
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>Total:</Typography>
                  </TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell></TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" sx={{ color: theme.palette.text.primary, fontWeight: 500 }}>
                      {formulationIngredients.reduce((sum, item) => sum + parseFloat(item.percentage || 0), 0).toFixed(2)}%
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="subtitle2" sx={{ fontWeight: 500, color: theme.palette.text.primary }}>
                      {formulation.target_batch_size} {formulation.unit_of_measure}
                    </Typography>
                  </TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Ingredient Selection Dialog */}
      <Dialog
        open={ingredientSelectionDialogOpen}
        onClose={() => {
          setIngredientSelectionDialogOpen(false);
          setIngredientSearchQuery('');
        }}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          Select Ingredient
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Box sx={{ mb: 4, mt: 3 }}>
            <TextField
              fullWidth
              placeholder="Search ingredients..."
              value={ingredientSearchQuery}
              onChange={(e) => setIngredientSearchQuery(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  '& fieldset': {
                    borderColor: alpha(theme.palette.primary.main, 0.3),
                    borderWidth: 1
                  },
                  '&:hover fieldset': {
                    borderColor: theme.palette.primary.main
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: theme.palette.primary.main
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            maxHeight: 400, 
            overflowY: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
            borderRadius: 2,
            background: 'white',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none'
          }}>
            {availableIngredients
              .filter(ingredient => 
                ingredient.name.toLowerCase().includes(ingredientSearchQuery.toLowerCase()) ||
                (ingredient.cas_number && ingredient.cas_number.toLowerCase().includes(ingredientSearchQuery.toLowerCase())) ||
                (ingredient.description && ingredient.description.toLowerCase().includes(ingredientSearchQuery.toLowerCase()))
              )
              .map(ingredient => (
                <Box
                  key={ingredient.id}
                  onClick={() => {
                    setSelectedIngredient(prev => ({
                      ...prev,
                      ingredient_id: ingredient.id
                    }));
                    setIngredientSelectionDialogOpen(false);
                    setIngredientSearchQuery('');
                    fetchIngredientSuppliersAndAutoSelect(ingredient.id);
                  }}
                  sx={{
                    p: 3,
                    borderBottom: `1px solid ${alpha(theme.palette.grey[200], 0.8)}`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04),
                      transform: 'translateX(4px)',
                      boxShadow: 'inset 4px 0 0 black'
                    },
                    '&:last-child': {
                      borderBottom: 'none'
                    }
                  }}
                >
                  <Typography variant="subtitle1" sx={{ fontWeight: 500, mb: 0.5 }}>
                    {ingredient.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                    {ingredient.cas_number && (
                      <Typography variant="body2" color="text.secondary">
                        CAS: {ingredient.cas_number}
                      </Typography>
                    )}
                    {ingredient.category && (
                      <Typography variant="body2" color="text.secondary">
                        Category: {ingredient.category}
                      </Typography>
                    )}
                  </Box>
                  {ingredient.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {ingredient.description}
                    </Typography>
                  )}
                </Box>
              ))
            }
            {availableIngredients
              .filter(ingredient => 
                ingredient.name.toLowerCase().includes(ingredientSearchQuery.toLowerCase()) ||
                (ingredient.cas_number && ingredient.cas_number.toLowerCase().includes(ingredientSearchQuery.toLowerCase())) ||
                (ingredient.description && ingredient.description.toLowerCase().includes(ingredientSearchQuery.toLowerCase()))
              ).length === 0 && (
              <Box sx={{ p: 6, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary">
                  No ingredients found matching your search.
                </Typography>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => {
              window.open(`/company/${formulation.company_id}/inventory/raw-materials`, '_blank');
            }}
            variant="outlined"
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              borderColor: alpha(theme.palette.primary.main, 0.3),
              color: theme.palette.primary.main,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                borderColor: theme.palette.primary.main,
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Add New Chemical
          </Button>
          <Button 
            onClick={() => {
              setIngredientSelectionDialogOpen(false);
              setIngredientSearchQuery('');
            }}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>

      {/* Phase Dialog */}
      <Dialog 
        open={phaseDialogOpen} 
        onClose={() => setPhaseDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          {phaseDialogMode === 'create' ? 'Add Phase' : 'Edit Phase'}
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Phase Name"
                value={selectedPhaseData.name || ''}
                onChange={handlePhaseChange}
                fullWidth
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={selectedPhaseData.description || ''}
                onChange={handlePhaseChange}
                fullWidth
                multiline
                rows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => setPhaseDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSavePhase}
            disabled={loading || !selectedPhaseData.name}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              '&:disabled': {
                background: theme.palette.grey[400],
                color: theme.palette.grey[100]
              }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ingredient Dialog */}
      <Dialog 
        open={ingredientDialogOpen} 
        onClose={() => setIngredientDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          {ingredientDialogMode === 'create' ? 'Add Ingredient' : 'Edit Ingredient'}
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <Button 
                fullWidth 
                variant="outlined" 
                onClick={() => setIngredientSelectionDialogOpen(true)}
                sx={{ 
                  borderRadius: 2,
                  py: 2,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  backdropFilter: 'blur(10px)',
                  borderColor: alpha(theme.palette.primary.main, 0.3),
                  color: selectedIngredient.ingredient_id ? theme.palette.text.primary : theme.palette.text.secondary,
                  fontWeight: 500,
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  '&:hover': {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: alpha(theme.palette.primary.main, 0.04)
                  }
                }}
              >
                {selectedIngredient.ingredient_id 
                  ? availableIngredients.find(ing => ing.id === selectedIngredient.ingredient_id)?.name || 'Select Ingredient'
                  : 'Select Ingredient'
                }
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                name="percentage"
                label="Percentage (%)"
                value={selectedIngredient.percentage || ''}
                onChange={handleIngredientChange}
                fullWidth
                type="number"
                inputProps={{ step: '0.01', min: '0', max: '100' }}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                name="function"
                label="Function"
                value={selectedIngredient.function || ''}
                onChange={handleIngredientChange}
                fullWidth
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Notes"
                value={selectedIngredient.notes || ''}
                onChange={handleIngredientChange}
                fullWidth
                multiline
                rows={3}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => setIngredientDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSaveIngredient}
            disabled={loading || !selectedIngredient.ingredient_id || !selectedIngredient.percentage}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              '&:disabled': {
                background: theme.palette.grey[400],
                color: theme.palette.grey[100]
              }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmationOpen}
        onClose={() => setDeleteConfirmationOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.grey[900], 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Delete sx={{ color: 'error.main', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
              Confirm Delete
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.primary', mt: 2 }}>
            Are you sure you want to delete this phase? This will also delete all ingredients in this phase.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid', 
          borderColor: 'divider', 
          p: 2, 
          justifyContent: 'space-between' 
        }}>
          <Button 
            onClick={() => setDeleteConfirmationOpen(false)}
            sx={{ 
              px: 3,
              borderRadius: 3,
              py: 1.5
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleDeletePhase}
            disabled={loading}
            sx={{
              px: 3,
              borderRadius: 3,
              py: 1.5,
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'error.dark',
                transform: 'translateY(-1px)'
              }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Delete Phase'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Ingredient Confirmation Dialog */}
      <Dialog
        open={deleteIngredientConfirmOpen}
        onClose={() => setDeleteIngredientConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.grey[900], 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.error.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.error.main, 0.08)}, ${alpha(theme.palette.error.main, 0.04)})`,
          display: 'flex',
          alignItems: 'center',
          gap: 2
        }}>
          <Box sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.error.main, 0.1),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <Delete sx={{ color: 'error.main', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem', color: 'text.primary' }}>
              Confirm Delete
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mt: 0.5 }}>
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Typography variant="body1" sx={{ lineHeight: 1.6, color: 'text.primary', mt: 2 }}>
            Are you sure you want to delete this ingredient from the formulation?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          borderTop: '1px solid', 
          borderColor: 'divider', 
          p: 2, 
          justifyContent: 'space-between' 
        }}>
          <Button 
            onClick={() => setDeleteIngredientConfirmOpen(false)}
            sx={{ 
              px: 3,
              borderRadius: 3,
              py: 1.5
            }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleDeleteIngredient}
            disabled={loading}
            sx={{
              px: 3,
              borderRadius: 3,
              py: 1.5,
              bgcolor: 'error.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'error.dark',
                transform: 'translateY(-1px)'
              }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Delete Ingredient'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* New Ingredient Dialog */}
      <Dialog 
        open={newIngredientDialogOpen} 
        onClose={() => setNewIngredientDialogOpen(false)}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 3,
            border: 'none',
            background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.9)}, ${alpha(theme.palette.grey[100], 0.9)})`,
            backdropFilter: 'blur(20px)',
            boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{ 
          p: 4, 
          borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
          color: theme.palette.text.primary,
          fontWeight: 500
        }}>
          Create New Ingredient
        </DialogTitle>
        <DialogContent sx={{ p: 4 }}>
          <Grid container spacing={3} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                name="name"
                label="Ingredient Name"
                value={newIngredientData.name}
                onChange={handleNewIngredientChange}
                fullWidth
                required
                helperText="Required"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="inci_name"
                label="INCI Name"
                value={newIngredientData.inci_name}
                onChange={handleNewIngredientChange}
                fullWidth
                helperText="Optional: International Nomenclature of Cosmetic Ingredients"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="cas_number"
                label="CAS Number"
                value={newIngredientData.cas_number}
                onChange={handleNewIngredientChange}
                fullWidth
                helperText="Optional: Chemical Abstracts Service registry number"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="description"
                label="Description"
                value={newIngredientData.description}
                onChange={handleNewIngredientChange}
                fullWidth
                multiline
                rows={3}
                helperText="Optional: Enter any additional details about this ingredient"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    backdropFilter: 'blur(10px)',
                    '& fieldset': {
                      borderColor: alpha(theme.palette.primary.main, 0.3),
                      borderWidth: 1
                    },
                    '&:hover fieldset': {
                      borderColor: theme.palette.primary.main
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ 
          p: 4, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
        }}>
          <Button 
            onClick={() => setNewIngredientDialogOpen(false)}
            sx={{ 
              borderRadius: 2,
              px: 3,
              py: 1.5,
              color: theme.palette.text.secondary,
              fontWeight: 500,
              background: 'rgba(255, 255, 255, 0.8)',
              backdropFilter: 'blur(10px)',
              '&:hover': {
                backgroundColor: alpha(theme.palette.primary.main, 0.04)
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleCreateNewIngredient}
            disabled={loading || !newIngredientData.name}
            variant="contained"
            sx={{
              borderRadius: 2,
              px: 4,
              py: 1.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              color: 'white',
              fontWeight: 500,
              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.4)}`
              },
              '&:disabled': {
                background: theme.palette.grey[400],
                color: theme.palette.grey[100]
              }
            }}
          >
            {loading ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Create Ingredient'}
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Box>
  );
};

export default IngredientsTab;